/**
 * Phase 3: review points, approval readiness, signatories, finalisation and revised accounts.
 * Mirrors Capium.AccountsProduction.Contracts.Phase3.
 */

/** 0 Draft, 1 In preparation, 2 Ready for review, 3 Under review, 4 Approved, 5 Finalised, 6 Filed. */
export enum PeriodStatus {
  Draft = 0,
  InPreparation = 1,
  ReadyForReview = 2,
  UnderReview = 3,
  Approved = 4,
  Finalised = 5,
  Filed = 6,
}

export const PERIOD_STATUS_LABELS: Record<number, string> = {
  0: 'Draft',
  1: 'In preparation',
  2: 'Ready for review',
  3: 'Under review',
  4: 'Approved',
  5: 'Finalised',
  6: 'Filed',
};

/** A required signatory that has not signed yet; blocks submission and the tax handoff. */
export interface MissingSignatory {
  reportRole: string;
  minimumCount: number;
  signedCount: number;
  requiresSignatureDate: boolean;
  description: string;
}

export interface ApprovalState {
  accountingPeriodId: string;
  clientId: string;
  periodStatus: number;
  balanceVersion: number;
  isReadyForApproval: boolean;
  readyDocumentVersionId: string | null;
  readyDataVersion: number | null;
  readyAtUtc: string | null;
  readyBy: string | null;
  clientApprovedAtUtc: string | null;
  clientApprovedName: string | null;
  accountantApprovedAtUtc: string | null;
  clearedCount: number;
  lastClearedAtUtc: string | null;
  lastClearedReason: string | null;
  /** The data moved after readiness was set, so the pinned version is out of date. */
  isStale: boolean;
  openReviewPoints: number;
  activeSignatories: number;
  finalDocumentVersionId: string | null;
  finalisedAtUtc: string | null;
  missingSignatories: MissingSignatory[];
}

export interface SetReadyPayload {
  documentVersionId: string;
  allowOpenReviewPoints: boolean;
}

export interface ClearReadyPayload {
  reason: string | null;
}

export interface ClearReadyResult {
  accountingPeriodId: string;
  signatoriesInvalidated: number;
  state: ApprovalState;
}

export interface RecordApprovalPayload {
  approvalType: 'Client' | 'Accountant';
  approvedName: string | null;
}

export interface ApprovalEvent {
  approvalEventId: number;
  accountingPeriodId: string;
  revisionCaseId: string | null;
  eventType: string;
  documentVersionId: string | null;
  dataVersion: number | null;
  reason: string | null;
  detailJson: string | null;
  occurredAtUtc: string;
  actorId: string;
  actorName: string | null;
}

/* ---------------------------------------------------------------- Signatories */

export const SIGNATORY_ROLES = ['DIRECTOR', 'MEMBER', 'PARTNER', 'SECRETARY', 'ACCOUNTANT'] as const;

export const SIGNATORY_ROLE_LABELS: Record<string, string> = {
  DIRECTOR: 'Director',
  MEMBER: 'Member',
  PARTNER: 'Partner',
  SECRETARY: 'Secretary',
  ACCOUNTANT: 'Accountant',
};

/** Only the accountant signs without a relationship on the client record. */
export function roleRequiresRelationship(role: string): boolean {
  return (role ?? '').toUpperCase() !== 'ACCOUNTANT';
}

export interface Signatory {
  signatoryId: string;
  accountingPeriodId: string;
  revisionCaseId: string | null;
  reportRole: string;
  reportContactId: string | null;
  personName: string;
  signatureDate: string | null;
  status: string;
  invalidatedAtUtc: string | null;
  invalidatedReason: string | null;
}

/** A person who may sign, taken from the client's real report contacts. */
export interface SignatoryCandidate {
  reportContactId: string;
  fullName: string;
  contactType: number;
  defaultRole: string | null;
  dateAppointed: string | null;
  dateCeased: string | null;
}

export interface SignatoryInput {
  signatoryId: string | null;
  reportRole: string;
  reportContactId: string | null;
  personName: string | null;
  signatureDate: string | null;
}

export interface SaveSignatoriesPayload {
  revisionCaseId: string | null;
  signatories: SignatoryInput[];
  removeMissing: boolean;
}

export interface SignatoriesView {
  accountingPeriodId: string;
  revisionCaseId: string | null;
  signatories: Signatory[];
  candidates: SignatoryCandidate[];
  missing: MissingSignatory[];
}

/* ---------------------------------------------------------------- Revisions */

/** 0 Replacement accounts, 1 Supplementary note. */
export const REVISION_TYPES = [
  { value: 0, label: 'Replacement accounts' },
  { value: 1, label: 'Supplementary note' },
];

export interface RevisionCase {
  revisionCaseId: string;
  accountingPeriodId: string;
  clientId: string;
  originalFinalVersionId: string;
  originalContentHash: string;
  originalVersionNo: number | null;
  revisionNo: number;
  supersedesRevisionCaseId: string | null;
  revisionType: number | null;
  reason: string | null;
  supplementaryNoteField: string | null;
  supplementaryNoteDate: string | null;
  status: string;
  revisedDocumentVersionId: string | null;
  revisedContentHash: string | null;
  revisedVersionNo: number | null;
  signatoryCount: number;
  createdAtUtc: string;
  finalisedAtUtc: string | null;
}

export interface CreateRevisionPayload {
  reason: string | null;
  revisionType: number | null;
}

export interface FinaliseRevisionPayload {
  documentVersionId: string;
  comment: string | null;
}

export interface FinaliseRevisionResult {
  revisionCaseId: string;
  documentVersionId: string;
  contentHash: string;
  finalisationSnapshotId: string;
  finalisedAtUtc: string;
  originalFinalVersionId: string;
  originalContentHash: string;
}

/* ---------------------------------------------------------------- Finalisation */

export interface FinalisationSnapshot {
  finalisationSnapshotId: string;
  accountingPeriodId: string;
  revisionCaseId: string | null;
  documentVersionId: string;
  versionNo: number;
  contentHash: string;
  dataVersion: number;
  regulatoryPackVersionId: string;
  templatePackId: string | null;
  taxonomyVersionId: string | null;
  signatoriesJson: string | null;
  approvalJson: string | null;
  storageRef: string | null;
  createdAtUtc: string;
}

/* ---------------------------------------------------------------- Roll-forward */

export interface RollForwardRun {
  rollForwardRunId: string;
  sourceAccountingPeriodId: string;
  newAccountingPeriodId: string;
  newStartDate: string | null;
  newEndDate: string | null;
  mappingsCopied: number;
  disclosuresCopied: number;
  schedulesCopied: number;
  settingsCopied: number;
  comparativesCarried: number;
  createdAtUtc: string;
}

export interface RollForwardPayload {
  startDate: string;
  endDate: string;
  copyMappings: boolean;
  copyDisclosures: boolean;
  copySchedules: boolean;
}

export interface RollForwardResult {
  sourceAccountingPeriodId: string;
  newAccountingPeriodId: string;
  rollForwardRunId: string;
  mappingsCopied: number;
  disclosuresCopied: number;
}

/* ---------------------------------------------------------------- Review workflow */

export interface ReviewPointResponse {
  reviewPointResponseId: number;
  reviewPointId: string;
  responseText: string;
  respondedBy: string;
  respondedByName: string | null;
  respondedAtUtc: string;
}

export interface ReviewPointDetail {
  reviewPointId: string;
  accountingPeriodId: string;
  objectType: string;
  objectId: string;
  text: string;
  assignedTo: string | null;
  status: string;
  raisedBy: string;
  raisedAtUtc: string;
  clearedBy: string | null;
  clearedAtUtc: string | null;
  /** The figures moved after this point was raised or cleared. */
  isStale: boolean;
  responses: ReviewPointResponse[];
}

export interface RaiseReviewPointPayload {
  objectType: string;
  objectId: string;
  text: string;
  assignedTo: string | null;
}

/**
 * ap5-a6 (item 113): the generated accounts documents for a period, so the revision can be
 * finalised by picking the approved version instead of typing its GUID.
 * type: 0 DraftAccounts, 1 FinalAccounts, 2 Ixbrl, 3 Evidence, 4 Acknowledgement.
 * status: 0 Requested, 1 Generating, 2 Generated, 3 Approved, 4 Final, 5 Failed.
 */
export interface DocumentVersionRow {
  documentVersionId: string;
  accountingPeriodId: string;
  versionNo: number;
  type: number;
  status: number;
  templatePackId: string | null;
  rulePackId: string | null;
  taxonomyVersionId: string | null;
  contentHash: string | null;
  storageRef: string | null;
  pageCount: number | null;
  dataVersion: number;
  requestedAtUtc: string;
  generatedAtUtc: string | null;
  failureReason: string | null;
  /** MEMBERS (Full), ABRIDGED or FILLETED; absent from an API that predates output profiles. */
  outputProfile?: string | null;
}

/** APT352: the stored validation run finalising reads (POST periods/{id}/validations); the Validation Centre's Run validations makes it. */
export interface ValidationRunSummary {
  runAtUtc: string;
  blockingCount: number;
  warningCount: number;
  informationCount: number;
  canFinalise: boolean;
}
